import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authFetch, authJsonFetch } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Database, 
  Search, 
  Play, 
  AlertTriangle,
  FileJson,
  Loader2,
  Eye,
  Trash2,
  Edit,
  Plus,
  RefreshCw,
  Terminal,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const GodModePage = () => {
  const { user } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  
  // Query form state
  const [selectedCollection, setSelectedCollection] = useState('');
  const [operation, setOperation] = useState('find');
  const [queryJson, setQueryJson] = useState('{}');
  const [updateJson, setUpdateJson] = useState('');
  const [limit, setLimit] = useState(20);
  const [skip, setSkip] = useState(0);
  
  // Results state
  const [results, setResults] = useState(null);
  const [schema, setSchema] = useState(null);
  
  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });

  // Check admin access
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchCollections();
    }
  }, [isAdmin]);

  const fetchCollections = async () => {
    try {
      const response = await authFetch(`${API}/godmode/collections`);
      if (response.ok) {
        const data = await response.json();
        setCollections(data.collections || []);
      }
    } catch (err) {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchema = async (collectionName) => {
    try {
      const response = await authFetch(`${API}/godmode/schema/${collectionName}`);
      if (response.ok) {
        const data = await response.json();
        setSchema(data);
      }
    } catch (err) {
      console.error('Failed to fetch schema');
    }
  };

  const handleCollectionChange = (value) => {
    setSelectedCollection(value);
    setResults(null);
    setSchema(null);
    if (value) {
      fetchSchema(value);
    }
  };

  const validateJson = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  const executeQuery = async () => {
    if (!selectedCollection) {
      toast.error('Please select a collection');
      return;
    }
    
    if (!validateJson(queryJson)) {
      toast.error('Invalid query JSON');
      return;
    }
    
    if ((operation === 'update' || operation === 'insert') && !validateJson(updateJson)) {
      toast.error('Invalid update/insert JSON');
      return;
    }

    // Confirm destructive operations
    if (operation === 'delete' || operation === 'update') {
      setConfirmDialog({
        open: true,
        action: async () => {
          await performQuery();
        }
      });
      return;
    }

    await performQuery();
  };

  const performQuery = async () => {
    setExecuting(true);
    try {
      const body = {
        collection: selectedCollection,
        operation,
        query: JSON.parse(queryJson),
        limit,
        skip
      };
      
      if (operation === 'update' || operation === 'insert') {
        body.update_data = JSON.parse(updateJson);
      }

      const response = await authJsonFetch(`${API}/godmode/query`, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (response.ok) {
        setResults(data);
        toast.success(`${operation} executed successfully`);
        
        // Refresh collections list after mutations
        if (['update', 'delete', 'insert'].includes(operation)) {
          fetchCollections();
        }
      } else {
        toast.error(data.detail || 'Query failed');
        setResults({ error: data.detail });
      }
    } catch (err) {
      toast.error('Query failed');
      setResults({ error: err.message });
    } finally {
      setExecuting(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  // Non-admin view
  if (!isAdmin) {
    return (
      <div className="p-8" data-testid="godmode-page">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="text-center">
              <Shield className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">
                God Mode is only available to administrators.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="godmode-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Terminal className="w-8 h-8 text-red-500" />
            God Mode
          </h1>
          <p className="text-muted-foreground mt-1">
            Direct database access for administrators
          </p>
        </div>
        <Badge variant="destructive" className="text-sm py-1 px-3">
          <AlertTriangle className="w-4 h-4 mr-1" />
          Admin Only
        </Badge>
      </div>

      {/* Warning Banner */}
      <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">
                Use with extreme caution
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Direct database modifications can corrupt data or break the application. 
                All actions are logged. Prefer using the standard UI for routine operations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Collections Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Collections
            </CardTitle>
            <CardDescription>
              {collections.length} collections in database
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {collections.map((coll) => (
                  <button
                    key={coll.name}
                    onClick={() => handleCollectionChange(coll.name)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors",
                      selectedCollection === coll.name
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    <span className="font-mono text-sm">{coll.name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {coll.document_count}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Query Panel */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Query Builder
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Collection</Label>
                <Select value={selectedCollection} onValueChange={handleCollectionChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select collection" />
                  </SelectTrigger>
                  <SelectContent>
                    {collections.map((coll) => (
                      <SelectItem key={coll.name} value={coll.name}>
                        {coll.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Operation</Label>
                <Select value={operation} onValueChange={setOperation}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="find">
                      <span className="flex items-center gap-2">
                        <Eye className="w-4 h-4" /> find
                      </span>
                    </SelectItem>
                    <SelectItem value="findOne">
                      <span className="flex items-center gap-2">
                        <Eye className="w-4 h-4" /> findOne
                      </span>
                    </SelectItem>
                    <SelectItem value="count">
                      <span className="flex items-center gap-2">
                        <Database className="w-4 h-4" /> count
                      </span>
                    </SelectItem>
                    <SelectItem value="update">
                      <span className="flex items-center gap-2 text-amber-600">
                        <Edit className="w-4 h-4" /> update
                      </span>
                    </SelectItem>
                    <SelectItem value="delete">
                      <span className="flex items-center gap-2 text-red-600">
                        <Trash2 className="w-4 h-4" /> delete
                      </span>
                    </SelectItem>
                    <SelectItem value="insert">
                      <span className="flex items-center gap-2 text-green-600">
                        <Plus className="w-4 h-4" /> insert
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Query Filter (JSON)</Label>
              <Textarea
                value={queryJson}
                onChange={(e) => setQueryJson(e.target.value)}
                className={cn(
                  "font-mono text-sm",
                  !validateJson(queryJson) && queryJson !== '{}' && "border-red-500"
                )}
                rows={4}
                placeholder='{"field": "value"}'
              />
              {schema && (
                <p className="text-xs text-muted-foreground mt-1">
                  Fields: {Object.keys(schema.schema || {}).join(', ')}
                </p>
              )}
            </div>

            {(operation === 'update' || operation === 'insert') && (
              <div>
                <Label>
                  {operation === 'update' ? 'Update Data (JSON with $set, etc.)' : 'Document to Insert (JSON)'}
                </Label>
                <Textarea
                  value={updateJson}
                  onChange={(e) => setUpdateJson(e.target.value)}
                  className={cn(
                    "font-mono text-sm",
                    updateJson && !validateJson(updateJson) && "border-red-500"
                  )}
                  rows={4}
                  placeholder={operation === 'update' 
                    ? '{"$set": {"field": "value"}}'
                    : '{"field": "value"}'
                  }
                />
              </div>
            )}

            {operation === 'find' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Limit</Label>
                  <Input
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value) || 20)}
                    min={1}
                    max={1000}
                  />
                </div>
                <div>
                  <Label>Skip</Label>
                  <Input
                    type="number"
                    value={skip}
                    onChange={(e) => setSkip(parseInt(e.target.value) || 0)}
                    min={0}
                  />
                </div>
              </div>
            )}

            <Button
              onClick={executeQuery}
              disabled={executing || !selectedCollection}
              className={cn(
                "w-full",
                operation === 'delete' && "bg-red-600 hover:bg-red-700",
                operation === 'update' && "bg-amber-600 hover:bg-amber-700"
              )}
            >
              {executing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Execute {operation}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Results Panel */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileJson className="w-5 h-5" />
                Results
              </span>
              {results.count !== undefined && (
                <Badge variant="outline">
                  {results.count} / {results.total || results.count} documents
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {results.error ? (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-600 font-medium">Error</p>
                <p className="text-sm text-red-500 mt-1">{results.error}</p>
              </div>
            ) : results.data ? (
              Array.isArray(results.data) ? (
                <div className="overflow-x-auto">
                  <pre className="bg-muted p-4 rounded-lg text-xs font-mono overflow-auto max-h-[500px]">
                    {JSON.stringify(results.data, null, 2)}
                  </pre>
                </div>
              ) : (
                <pre className="bg-muted p-4 rounded-lg text-xs font-mono overflow-auto max-h-[500px]">
                  {JSON.stringify(results.data, null, 2)}
                </pre>
              )
            ) : (
              <pre className="bg-muted p-4 rounded-lg text-xs font-mono">
                {JSON.stringify(results, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>
      )}

      {/* Schema Panel */}
      {schema && schema.sample && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Sample Document Schema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(schema.schema || {}).map(([field, type]) => (
                <div key={field} className="bg-muted rounded px-2 py-1">
                  <span className="font-mono text-xs">{field}</span>
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    {type}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm {operation} Operation
            </AlertDialogTitle>
            <AlertDialogDescription>
              {operation === 'delete' 
                ? 'This will permanently delete matching documents. This action cannot be undone.'
                : 'This will modify matching documents. Make sure your query and update data are correct.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDialog.action && confirmDialog.action()}
              className={cn(
                operation === 'delete' ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"
              )}
            >
              Confirm {operation}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
